import { useQuery } from "@tanstack/react-query";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Link as LinkIcon, MousePointerClick, Eye as EyeIcon, TrendingUp, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function fmtInt(n) {
  return new Intl.NumberFormat("en-US").format(Math.round(n || 0));
}

export default function GscPanel() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();

  const { data, isLoading } = useQuery({
    queryKey: ["gsc-summary", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/gsc/summary`)).data,
    enabled: !!selectedShopId,
    retry: false,
  });

  if (isLoading) return null;

  // Not connected → CTA card
  if (!data?.connected) {
    return (
      <div className="border border-dashed border-border bg-card rounded-lg p-5 flex items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-md bg-secondary grid place-items-center shrink-0">
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm">
              {t("gsc_connect_title") || "Google Search Console verbinden"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data?.reason === "Not connected"
                ? t("gsc_connect_hint") ||
                  "Verbinde dein GSC-Konto in den Einstellungen, um echte Impressionen & Clicks zu sehen."
                : data?.reason || "Google not configured"}
            </div>
          </div>
        </div>
        <Link
          to="/settings"
          className="text-xs font-mono uppercase tracking-widest bg-foreground text-background px-3 py-2 rounded-md hover:opacity-90 whitespace-nowrap"
          data-testid="gsc-connect-cta"
        >
          {t("nav_settings")} →
        </Link>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="border border-destructive/40 bg-destructive/5 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-destructive" />
          <div className="font-medium text-sm">GSC verbunden — aber kein Zugriff auf Property</div>
        </div>
        <div className="text-xs font-mono text-muted-foreground">
          {data.site_url} · {data.hint || data.error}
        </div>
      </div>
    );
  }

  const t2 = data.totals || {};

  return (
    <div data-testid="dashboard-gsc-panel" className="border border-border bg-card rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[hsl(var(--chart-3))]" />
            Google Search Console · LIVE
          </div>
          <h2 className="font-display text-lg font-medium tracking-tight mt-1">
            {selectedShop?.domain} · 28-day performance
          </h2>
        </div>
        <a
          href={`https://search.google.com/search-console?resource_id=sc-domain%3A${selectedShop?.domain}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          Open in GSC <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <GscTile label="Clicks" value={fmtInt(t2.clicks)} icon={MousePointerClick} accent="hsl(var(--chart-1))" />
        <GscTile label="Impressions" value={fmtInt(t2.impressions)} icon={EyeIcon} accent="hsl(var(--chart-2))" />
        <GscTile label="CTR" value={`${t2.ctr}%`} icon={TrendingUp} accent="hsl(var(--chart-3))" />
        <GscTile label="Avg. position" value={t2.position} icon={TrendingUp} accent="hsl(var(--chart-4))" />
      </div>

      {data.daily?.length > 0 && (
        <div className="w-full h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                tickFormatter={(d) => d.slice(5)}
                interval={3}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "IBM Plex Mono" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                  fontFamily: "IBM Plex Mono",
                }}
              />
              <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="hsl(var(--chart-1))" strokeWidth={2} dot={false} />
              <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="hsl(var(--chart-2))" strokeWidth={1.5} strokeDasharray="3 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.top_queries?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-3">
            Top Queries · 28d
          </div>
          <ul className="divide-y divide-border/70">
            {data.top_queries.slice(0, 8).map((q, i) => (
              <li key={i} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 py-2 text-sm items-center">
                <span className="truncate">{q.query}</span>
                <span className="font-mono tabular text-xs text-muted-foreground w-16 text-right">
                  {fmtInt(q.clicks)} clicks
                </span>
                <span className="font-mono tabular text-xs text-muted-foreground w-24 text-right">
                  {fmtInt(q.impressions)} impr
                </span>
                <span className="font-mono tabular text-xs w-14 text-right">
                  {q.ctr}%
                </span>
                <span
                  className={cn(
                    "font-mono tabular text-xs w-10 text-right",
                    q.position <= 3
                      ? "text-[hsl(var(--chart-3))]"
                      : q.position <= 10
                        ? "text-[hsl(var(--chart-1))]"
                        : "text-muted-foreground",
                  )}
                >
                  #{q.position}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function GscTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="border border-border rounded-md p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">{label}</div>
        <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
      </div>
      <div className="font-mono text-2xl tabular tracking-tight mt-3">{value}</div>
    </div>
  );
}
